import { listQueued, markDone, markFailed } from './outbox';
import { createPosInvoice, submitPosInvoice } from '../api/invoice';

/**
 * Flush queued offline operations to the server.
 *
 * Currently supports one operation kind:
 *   - "pos_invoice": creates + submits a POS Invoice via Frappe API.
 *
 * Each operation is processed individually so partial failures
 * don't block the rest of the queue.
 */
export async function flushOutboxOnce() {
  const queued = await listQueued();
  if (!queued.length) return { flushed: 0, failed: 0 };

  let flushed = 0;
  let failed = 0;

  for (const op of queued) {
    try {
      if (op.kind === 'pos_invoice') {
        // Create the POS Invoice (Draft)
        const created = await createPosInvoice(op.payload);
        // Submit it (pass full doc to avoid TimestampMismatchError)
        await submitPosInvoice(created);
      }
      // If we add more operation kinds later, handle them here.

      // eslint-disable-next-line no-await-in-loop
      await markDone(op.id);
      flushed += 1;
    } catch (e) {
      const msg = e?.response?.data?._server_messages || e?.message || String(e);
      // eslint-disable-next-line no-await-in-loop
      await markFailed(op.id, msg);
      failed += 1;
    }
  }

  return { flushed, failed };
}

/**
 * Start a periodic sync timer.
 * Flushes the outbox every `intervalMs` when the browser is online.
 * Returns a cleanup function to clear the timer.
 */
export function startSyncTimer(intervalMs = 30_000) {
  const id = setInterval(() => {
    if (navigator.onLine) {
      flushOutboxOnce().catch(() => {});
    }
  }, intervalMs);

  // Also flush immediately when we come back online
  const onOnline = () => {
    flushOutboxOnce().catch(() => {});
  };
  window.addEventListener('online', onOnline);

  return () => {
    clearInterval(id);
    window.removeEventListener('online', onOnline);
  };
}
